<script setup lang="ts">
  import { Button } from '@travelata-libs/ui-kit';
  import { useViewport, Breakpoint } from '@/shared/composables'
  import { computed, ref, watch } from 'vue';

  import { DateFromField, formatCheckInLabel } from '@/features/date-from';
  import type { DateFrom } from '@/features/date-from';
  import { DepartureCityField } from '@/features/departure-city';
  import type { DepartureCity } from '@/features/departure-city';
  import { DestinationField } from '@/features/destination';
  import type { DestinationPrices, Selection } from '@/features/destination';
  import { NightsRangeField } from '@/features/nights-range';
  import type { NightsRange } from '@/features/nights-range';
  import { TouristsRoomsField } from '@/features/tourists-rooms';
  import type { TouristsSelection } from '@/features/tourists-rooms';
  import { useTravelataAppConfig } from '@/shared/composables/useTravelataAppConfig';
  import { useCrossFieldCriteria } from '../model/useCrossFieldCriteria';
  import { useSearchFormData } from '../model/useSearchFormData';
  import type { SearchFormExpose } from '../model/types';
  import { SEARCH_FORM_TEXTS } from '../config/texts';

  interface SearchFormProps {
    prices?: DestinationPrices;
    fromCity?: DepartureCity | null;
    destination?: Selection | null;
    nights?: NightsRange | null;
    dateFrom?: DateFrom | null;
    tourists?: TouristsSelection | null;
    destinationDisabled?: boolean;
    isVisibleRoomButton?: boolean;
  }

  const props = withDefaults(defineProps<SearchFormProps>(), {
    prices: undefined,
    fromCity: null,
    destination: null,
    nights: null,
    dateFrom: null,
    tourists: null,
    destinationDisabled: false,
  });

  interface SearchFormEmits {
    (e: 'update:fromCity', city: DepartureCity | null): void;
    (e: 'update:destination', selection: Selection | null): void;
    (e: 'update:nights', range: NightsRange): void;
    (e: 'update:dateFrom', value: DateFrom | null): void;
    (e: 'update:tourists', tourists: TouristsSelection): void;
    (e: 'search'): void;
    (e: 'buy'): void;
  }

  const emit = defineEmits<SearchFormEmits>();

  const { countries, cities, defaultCriteria } = useSearchFormData();
  const { isSmaller } = useViewport();
  const animationsReady = ref(false);

  const DEFAULT_NIGHTS: NightsRange = { from: 7, to: 10 };
  function defaultTourists(): TouristsSelection {
    return { rooms: [{ adults: 2, childrenAges: [] }] };
  }
  const dateFrom = computed({
    get: () => props.dateFrom,
    set: (value) => emit('update:dateFrom', value), 
  });
  const tourists = computed({
    get: () => props.tourists || defaultTourists(),
    set: (value) => emit('update:tourists', value), 
  });
  const nights = computed({
    get: () => props.nights || DEFAULT_NIGHTS,
    set: (value) => emit('update:nights', value), 
  });
  const fromCity = computed({
    get: () => props.fromCity,
    set: (value) => emit('update:fromCity', value), 
  });
  const destination = computed({
    get: () => props.destination,
    set: (value) => emit('update:destination', value), 
  });

  const isAdaptive = isSmaller(Breakpoint.L);

  const searchButtonText = computed(() => isAdaptive.value ? '' : SEARCH_FORM_TEXTS.searchBtn);

  const criteria = useCrossFieldCriteria({
    countries,
    defaultCriteria,
    destination,
    nights,
    dateFrom,
  });

  const checkInLabel = computed<string | undefined>(() =>
    dateFrom.value ? formatCheckInLabel(dateFrom.value) : undefined,
  );

  function defaultCityId(): number {
    try {
      return useTravelataAppConfig().defaultDepartureCityId ?? 2;
    } catch {
      return 2;
    }
  }

  const originKey = ref(0);
  watch(
    cities,
    (list) => {
      if (fromCity.value === null && list.length > 0) {
        const found = list.find((city) => city.id === defaultCityId()) ?? null;
        if (found) {
          fromCity.value = found;
          criteria.applyInitialDate(found);
          originKey.value += 1;
        }
      }
    },
    { immediate: true },
  );

  function onFromCity(city: DepartureCity | null): void {
    fromCity.value = city;
    criteria.applyDefaults(city);
  }

  function onDestination(selection: Selection): void {
    destination.value = selection;
    criteria.applyDefaults(fromCity.value);
  }

  function onNights(range: NightsRange): void {
    nights.value = range;
  }

  function onDateFrom(value: DateFrom): void {
    dateFrom.value = value;
  }

  function onTourists(sel: TouristsSelection): void {
    tourists.value = sel;
  }

  type ValidatableField = { validate: () => boolean } | null;
  type DestinationFieldRef = { validate: () => boolean; clear: () => void } | null;
  const fromCityRef = ref<ValidatableField>(null);
  const destinationRef = ref<DestinationFieldRef>(null);
  const dateFromRef = ref<ValidatableField>(null);
  const nightsRef = ref<ValidatableField>(null);
  const touristsRef = ref<ValidatableField>(null);

  function validate(): boolean {
    const results = [
      fromCityRef.value?.validate() ?? true,
      destinationRef.value?.validate() ?? true,
      dateFromRef.value?.validate() ?? true,
      nightsRef.value?.validate() ?? true,
      touristsRef.value?.validate() ?? true,
    ];
    return results.every(Boolean);
  }

  function submit(): void {
    emit('search');
  }

  function reset(): void {
    fromCity.value = null;
    destination.value = null;
    destinationRef.value?.clear();
    nights.value = { ...DEFAULT_NIGHTS };
    dateFrom.value = null;
    tourists.value = defaultTourists();
    criteria.reset();
  }

  defineExpose<SearchFormExpose>({
    validate,
    submit,
    reset,
  });
</script>

<template>
  <div class="search-form" data-testid="search-form">
    <div class="search-form__bar">
      <div :class="['search-form__card']">
        <div class="search-form__cell search-form__cell_origin">
          <DepartureCityField
            ref="fromCityRef"
            :key="originKey"
            :model-value="fromCity"
            :cities="cities"
            @update:modelValue="onFromCity"
          />
        </div>

        <span class="search-form__separator" aria-hidden="true" />

        <div class="search-form__cell search-form__cell_destination">
          <DestinationField
            ref="destinationRef"
            :model-value="destination"
            :countries="countries"
            :disabled="destinationDisabled"
            :prices="props.prices"
            @update:modelValue="onDestination"
          />
        </div>

        <span class="search-form__separator" aria-hidden="true" />

        <div class="search-form__cell search-form__cell_date">
          <DateFromField 
            ref="dateFromRef" 
            :model-value="dateFrom" 
            @manual="criteria.markTouchedDate"
            @update:modelValue="onDateFrom" 
          />
        </div>

        <span class="search-form__separator" aria-hidden="true" />

        <div class="search-form__cell search-form__cell_nights">
          <NightsRangeField
            ref="nightsRef"
            :model-value="nights"
            :check-in-label="checkInLabel"
            @manual="criteria.markTouchedNights"
            @update:modelValue="onNights"
          />
        </div>

        <span class="search-form__separator" aria-hidden="true" />

        <div 
          :class="['search-form__cell', 'search-form__cell_guests', {
            'search-form__cell_guests-no-rooms': !isVisibleRoomButton,
          }]">
          <TouristsRoomsField ref="touristsRef" :is-visible-add-button="isVisibleRoomButton" :model-value="tourists" @update:modelValue="onTourists" />
        </div>
        <div class="search-form__btn-container">
          <Button 
            @click="emit('search')" 
            class="search-form__search-btn" 
            data-testid="search-form-search-btn"
            appearance="primary"
            :icon-left="isAdaptive ? 'search' : ''"
          >
            <template #default v-if="searchButtonText"> {{ searchButtonText }}  </template>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .search-form {
    $root: &;
    background: linear-gradient(262deg, var(--color-cyan-40, #2CE) -14.86%, var(--color-brand-secondary, #218BFF) 6.05%, var(--color-sky-50, #218BFF) 112.65%);
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    padding: var(--spacing-m) 0  var(--spacing-l) 0;
    width: 100%;


    &__bar {
      align-items: stretch;
      display: flex;
      gap: var(--spacing-xs);
      max-width: $max-width-xl;
      width: 100%;
    }

    &__card {
      align-items: center;
      background: var(--color-bg-primary);
      border-radius: var(--rounding-l);
      display: flex;
      flex: 1 1 auto;
      padding-right: var(--spacing-xs);
    }

    &__cell {
      flex-grow: 1;
      &_origin {
        flex-basis: 109px;
        max-width: 230px;

        :deep(.departure-city-field__control) {
          border-radius: var(--rounding-l) 0 0 var(--rounding-l);
        }
      }
      &_destination {
        flex-basis: 171px;
        max-width: 436px;
      }
      &_date {
        flex-basis: 124px;
        max-width: 172px;
      }
      &_nights {
        flex-basis: 124px;
        max-width: 173px;
      }
      &_guests {
        flex-basis: 116px;
        max-width: 163px;
      }
      &_guests-no-rooms {
        flex-basis: 141px;
        max-width: 230px;
        flex-grow: 2;
      }
    }


    &__separator {
      align-self: center;
      background-color: var(--color-neutral-20);
      flex: 0 0 1px;
      height: 54px;
      width: 1px;
    }

    &__btn-container {
      box-sizing: border-box;
      padding-left: var(--spacing-xs);
      width: 100%;
      max-width: 188px;
    }

    &__search-btn {
      background: linear-gradient(238deg, var(--color-yellow-40) 0%, var(--color-orange-40) 13%, var(--color-orange-70) 100%);
      width: 100%;
    }

    @media (max-width: $breakpoint-Xl) {
      &__bar {
        max-width: $max-width-l;
      }
    }

    @media (max-width: $breakpoint-l) {
      &__bar {
        max-width: $max-width-m;
      }
      &__btn-container {
        padding-left: 0;
        width: auto;
        max-width: min-content;
      }
      &__search-btn {
        width: auto;
      }
    }

    @media (max-width: $breakpoint-m) {
      &__bar {
        max-width: $max-width-s;
      }
    }
  }
</style>
